import Loader from '../../../components/common/loader/Loader'
import React, { FC, useContext, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import TableExtendedOld, {
  DEFAULT_PAGE_LIMIT
} from '../../../components/common/table-extended-old/TableExtendedOld'
import Tag from '../../../components/common/tag/Tag'
import Tooltip from '../../../components/common/tooltip/Tooltip'
import {
  CampaignAudienceTableOrderColumn,
  DashboardAudienceTableOrderColumn,
  DashboardUserEventTypeEnum,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { Column } from 'react-table'
import { formatDateShort } from 'front-end/src/utils/dateHelpers'
import { LinkableUserAvatar } from '../../../components/common/user/linkable-user-avatar/LinkableUserAvatar'
import { ManageDashboardPageContext } from '../manage/ManageDashboardPage'
import { useDashboardAudienceTableQuery } from './DashboardAudienceTable.codegen'
import { Box } from 'front-end/src/components'

const formatRowWithDate = (row) =>
  row.value ? formatDateShort(row.value) : null

export const DashboardAudienceTable: FC = () => {
  const { dashboard } = useContext(ManageDashboardPageContext)

  const [tableFilters, setTableFilters] = useState({
    page: 1,
    orderColumn: DashboardAudienceTableOrderColumn.timestamp,
    orderDirection: SqlOrderDirectionEnum.DESC,
    limit: DEFAULT_PAGE_LIMIT,
    search: ''
  })

  const { data, loading } = useDashboardAudienceTableQuery({
    variables: {
      id: dashboard.id,
      types: [
        DashboardUserEventTypeEnum.join,
        DashboardUserEventTypeEnum.leave
      ],
      offset: (tableFilters.page - 1) * DEFAULT_PAGE_LIMIT,
      ...tableFilters
    }
  })

  if (!data) {
    return <Loader />
  }

  const tableColumns: Column[] = [
    {
      Header: '',
      sortable: false,
      maxWidth: 54,
      Cell: (row) => {
        return <LinkableUserAvatar user={row.original.user} />
      }
    },
    {
      id: DashboardAudienceTableOrderColumn.display_name,
      Header: t`Name`,
      accessor: 'user',

      Cell: (row) => {
        return (
          <Tooltip text={row.original.user.email}>
            <strong className="dark">{row.original.user.display_name}</strong>
          </Tooltip>
        )
      }
    },
    {
      Header: t`Joined`,
      maxWidth: 150,
      accessor: 'timestamp',
      Cell: formatRowWithDate
    },

    {
      Header: t`Status`,
      sortable: false,
      accessor: 'type',
      minWidth: 65,
      maxWidth: 120,
      Cell: (row) => {
        return (
          <span className="green">
            {row.value === DashboardUserEventTypeEnum.join ? (
              <Tag color="blue">
                <Trans>Active</Trans>
              </Tag>
            ) : (
              <Tag color="red">
                <Trans>Left</Trans>
              </Tag>
            )}
          </span>
        )
      }
    }
  ]

  let pagesCount
  const { admin } = data
  if (admin && admin.dashboard) {
    pagesCount = Math.ceil(
      (admin.dashboard.audienceEventsCount || 0) / DEFAULT_PAGE_LIMIT
    )
  } else {
    pagesCount = 1
  }

  return (
    <Box className="CampaignAudience__table" mt="24px">
      <TableExtendedOld
        loading={loading}
        manual
        data={admin?.dashboard?.audienceEvents}
        tableHeader={t`Audience`}
        columns={tableColumns}
        className="-highlight AnalyticsTable"
        defaultPageSize={DEFAULT_PAGE_LIMIT}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={5}
        pages={pagesCount}
        defaultSortDesc
        defaultSorted={[
          {
            id: tableFilters.orderColumn,
            desc: tableFilters.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          // @ts-expect-error TODO: check typings
          setTableFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false
            let orderColumn = newSorted[0]
              ? newSorted[0].id
              : DashboardAudienceTableOrderColumn.display_name

            if (orderColumn === 'status' || orderColumn === 'joinTimestamp') {
              orderColumn = DashboardAudienceTableOrderColumn.timestamp
            }
            return {
              ...prevState,
              orderColumn: orderColumn as CampaignAudienceTableOrderColumn,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          setTableFilters((prevState) => {
            return {
              ...prevState,
              page: newPage + 1
            }
          })
        }}
        page={tableFilters.page - 1}
        onSearchChange={(searchVal) => {
          setTableFilters((prevState) => {
            return {
              ...prevState,
              search: searchVal
            }
          })
        }}
      />
    </Box>
  )
}
